import React, { useCallback, useMemo } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  formatTimeInputMask,
  normalizeSchedulePeriod,
  sortScheduleLinesChronologically,
} from '../../utils/scheduleTimeUtils';
import {
  newScheduleLineId,
  parseScheduleText,
  serializeScheduleDoc,
  SCHEDULE_LAST_ITEM_DEFAULT,
} from '../ScheduleEditor';

function newScheduleRow() {
  return {
    id: newScheduleLineId(),
    kind: 'schedule',
    time: '',
    period: 'AM',
    item: '',
    notes: '',
  };
}

export function appendDaySheetCue(value) {
  const { lines } = parseScheduleText(value);
  return serializeScheduleDoc({
    lines: [...lines, { ...newScheduleRow(), item: SCHEDULE_LAST_ITEM_DEFAULT }],
  });
}

export function appendDaySheetText(value) {
  const { lines } = parseScheduleText(value);
  return serializeScheduleDoc({
    lines: [...lines, { id: newScheduleLineId(), kind: 'text', text: '' }],
  });
}

/**
 * Paper-native schedule editor — rows match DaySheetPaper preview layout (item + time).
 */
export function DaySheetScheduleEditor({
  value,
  onChange,
  readOnly = false,
  compact = false,
  pal = null,
  simpleActions = false,
  hideFooter = false,
  typeScale = null,
}) {
  const styles = useMemo(
    () => createStyles(compact, pal, typeScale),
    [compact, pal, typeScale]
  );
  const ink = pal?.ink || '#111111';
  const mute = pal?.mute || '#333333';
  const faint = pal?.faint || '#999999';
  const alert = pal?.alert || '#a40000';
  const { lines } = useMemo(() => parseScheduleText(value), [value]);

  const pushLines = useCallback(
    (nextLines) => {
      onChange?.(serializeScheduleDoc({ lines: nextLines }));
    },
    [onChange]
  );

  const updateLine = useCallback(
    (id, patch, { sortByTime = false } = {}) => {
      let next = lines.map((ln) => (ln.id === id ? { ...ln, ...patch } : ln));
      if (sortByTime) {
        next = sortScheduleLinesChronologically(next);
      }
      pushLines(next);
    },
    [lines, pushLines]
  );

  const removeLine = useCallback(
    (id) => {
      pushLines(lines.filter((ln) => ln.id !== id));
    },
    [lines, pushLines]
  );

  const moveLine = useCallback(
    (index, delta) => {
      const j = index + delta;
      if (j < 0 || j >= lines.length) return;
      const next = [...lines];
      [next[index], next[j]] = [next[j], next[index]];
      pushLines(next);
    },
    [lines, pushLines]
  );

  const addRow = useCallback(() => {
    pushLines([...lines, { ...newScheduleRow(), item: SCHEDULE_LAST_ITEM_DEFAULT }]);
  }, [lines, pushLines]);

  const addTextRow = useCallback(() => {
    pushLines([...lines, { id: newScheduleLineId(), kind: 'text', text: '' }]);
  }, [lines, pushLines]);

  if (readOnly) return null;

  return (
    <View style={styles.wrap}>
      {lines.length === 0 ? (
        <Text style={styles.emptyHint}>No schedule items yet. Add a call time below.</Text>
      ) : null}
      {lines.map((ln, index) => {
        if (ln.kind === 'blank') {
          return (
            <View key={ln.id} style={styles.row}>
              <View style={styles.blankRule} />
              <RowActions
                styles={styles}
                index={index}
                total={lines.length}
                mute={mute}
                alert={alert}
                simpleActions={simpleActions}
                onMoveUp={() => moveLine(index, -1)}
                onMoveDown={() => moveLine(index, 1)}
                onRemove={() => removeLine(ln.id)}
              />
            </View>
          );
        }

        if (ln.kind === 'text') {
          return (
            <View key={ln.id} style={styles.row}>
              <TextInput
                style={[styles.itemInput, styles.textLineInput]}
                value={ln.text}
                onChangeText={(t) => updateLine(ln.id, { text: t })}
                placeholder="Text line"
                placeholderTextColor={faint}
                multiline={false}
              />
              <RowActions
                styles={styles}
                index={index}
                total={lines.length}
                mute={mute}
                alert={alert}
                simpleActions={simpleActions}
                onMoveUp={() => moveLine(index, -1)}
                onMoveDown={() => moveLine(index, 1)}
                onRemove={() => removeLine(ln.id)}
              />
            </View>
          );
        }

        if (ln.kind === 'schedule' || ln.kind === 'unset') {
          const period = normalizeSchedulePeriod(ln.period || 'AM');
          return (
            <View key={ln.id} style={styles.row}>
              <TextInput
                style={styles.itemInput}
                value={ln.item || ''}
                onChangeText={(t) => updateLine(ln.id, { kind: 'schedule', item: t })}
                placeholder="CALL TIME LABEL"
                placeholderTextColor={faint}
                autoCapitalize="characters"
              />
              <TextInput
                style={styles.timeInput}
                value={ln.time || ''}
                onChangeText={(t) =>
                  updateLine(ln.id, { kind: 'schedule', time: formatTimeInputMask(t) })
                }
                onBlur={() => updateLine(ln.id, {}, { sortByTime: true })}
                placeholder="0:00"
                placeholderTextColor={faint}
                keyboardType={Platform.OS === 'web' ? 'default' : 'numeric'}
              />
              <SchedulePeriodSelect
                period={period}
                compact={compact}
                ink={ink}
                fontSize={typeScale?.cueSize}
                onChange={(next) =>
                  updateLine(ln.id, { kind: 'schedule', period: next }, { sortByTime: true })
                }
              />
              <RowActions
                styles={styles}
                index={index}
                total={lines.length}
                mute={mute}
                alert={alert}
                simpleActions={simpleActions}
                onMoveUp={() => moveLine(index, -1)}
                onMoveDown={() => moveLine(index, 1)}
                onRemove={() => removeLine(ln.id)}
              />
            </View>
          );
        }

        return null;
      })}
      {hideFooter ? null : (
        <View style={styles.footer}>
          <TouchableOpacity style={styles.footerBtn} onPress={addRow} accessibilityLabel="Add call time">
            <Ionicons name="add" size={14} color={ink} />
            <Text style={styles.footerBtnText}>Add call time</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.footerBtnGhost} onPress={addTextRow} accessibilityLabel="Add text line">
            <Text style={styles.footerBtnGhostText}>+ Text line</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

function SchedulePeriodSelect({ period, onChange, compact, ink = '#000000', fontSize }) {
  const p = normalizeSchedulePeriod(period);
  const size = fontSize || (compact ? 11 : 12);
  if (Platform.OS === 'web') {
    return (
      <select
        value={p}
        onChange={(e) => onChange(normalizeSchedulePeriod(e.target.value))}
        style={{
          fontSize: size,
          fontWeight: 700,
          color: ink,
          border: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          padding: 0,
          margin: 0,
          width: Math.max(40, size * 2.6),
          flexShrink: 0,
          outline: 'none',
        }}
        aria-label="AM or PM"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    );
  }

  return (
    <TouchableOpacity
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1,
        flexShrink: 0,
        minWidth: compact ? 36 : 40,
      }}
      onPress={() => onChange(p === 'AM' ? 'PM' : 'AM')}
      accessibilityLabel={`Period ${p}. Tap to switch.`}
    >
      <Text
        style={{
          fontSize: fontSize || (compact ? 11 : 12),
          fontWeight: '700',
          color: ink,
        }}
      >
        {p}
      </Text>
      <Ionicons name="chevron-down" size={10} color={ink} />
    </TouchableOpacity>
  );
}

function RowActions({
  styles,
  index,
  total,
  onMoveUp,
  onMoveDown,
  onRemove,
  mute = '#333',
  alert = '#a40000',
  simpleActions = false,
}) {
  return (
    <View style={styles.rowActions}>
      {simpleActions ? null : (
        <>
          <TouchableOpacity
            style={[styles.rowActionBtn, index === 0 && styles.rowActionBtnDisabled]}
            onPress={onMoveUp}
            disabled={index === 0}
            hitSlop={4}
            accessibilityLabel="Move up"
          >
            <Ionicons name="chevron-up" size={12} color={mute} />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.rowActionBtn, index >= total - 1 && styles.rowActionBtnDisabled]}
            onPress={onMoveDown}
            disabled={index >= total - 1}
            hitSlop={4}
            accessibilityLabel="Move down"
          >
            <Ionicons name="chevron-down" size={12} color={mute} />
          </TouchableOpacity>
        </>
      )}
      <TouchableOpacity style={styles.rowActionBtn} onPress={onRemove} hitSlop={4} accessibilityLabel="Remove">
        <Ionicons name="trash-outline" size={12} color={alert} />
      </TouchableOpacity>
    </View>
  );
}

function createStyles(compact, pal, typeScale) {
  const itemSize = typeScale?.cueSize || (compact ? 12 : 14);
  const timeSize = itemSize;
  const rowH = typeScale?.rowH || (compact ? 22 : 26);
  const ink = pal?.ink || '#000000';
  const mute = pal?.mute || '#666666';
  const faint = pal?.faint || '#555555';
  const line = pal?.line || 'rgba(0,0,0,0.12)';
  const surf = pal?.surf || '#ffffff';
  const rule = pal?.lineSoft || pal?.line || '#cccccc';
  return StyleSheet.create({
    wrap: {
      width: '100%',
      flex: typeScale ? 1 : undefined,
      gap: 0,
    },
    emptyHint: {
      fontSize: Math.max(11, itemSize * 0.7),
      color: mute,
      fontStyle: 'italic',
      marginBottom: 6,
      textAlign: 'center',
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      minHeight: rowH,
      ...(typeScale ? { height: rowH } : { marginBottom: compact ? 1 : 4 }),
    },
    itemInput: {
      flex: 1,
      color: ink,
      fontSize: itemSize,
      fontWeight: '700',
      textTransform: 'uppercase',
      paddingVertical: typeScale ? 0 : (compact ? 1 : 2),
      paddingHorizontal: 0,
      borderWidth: 0,
      backgroundColor: 'transparent',
      ...(typeScale ? { minWidth: 0, lineHeight: itemSize * 1.15 } : {}),
      ...(Platform.OS === 'web' ? { outlineStyle: 'none', overflow: 'visible' } : {}),
    },
    textLineInput: {
      textTransform: 'none',
      fontWeight: '700',
    },
    timeInput: {
      width: Math.max(48, itemSize * 3.1),
      flexShrink: 0,
      color: ink,
      fontSize: timeSize,
      fontWeight: '700',
      textAlign: 'right',
      paddingVertical: typeScale ? 0 : (compact ? 1 : 2),
      paddingHorizontal: 0,
      borderWidth: 0,
      backgroundColor: 'transparent',
      lineHeight: typeScale ? itemSize * 1.15 : undefined,
      ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
    },
    rowActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 1,
      flexShrink: 0,
      opacity: 0.85,
    },
    rowActionBtn: {
      width: 20,
      height: 20,
      alignItems: 'center',
      justifyContent: 'center',
    },
    rowActionBtnDisabled: {
      opacity: 0.25,
    },
    blankRule: {
      flex: 1,
      height: 1,
      backgroundColor: rule,
      marginVertical: 4,
    },
    footer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: 8,
      marginTop: 8,
      paddingTop: 6,
      borderTopWidth: 1,
      borderTopColor: line,
    },
    footerBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: ink,
      backgroundColor: surf,
    },
    footerBtnText: {
      fontSize: 11,
      fontWeight: '800',
      color: ink,
    },
    footerBtnGhost: {
      paddingHorizontal: 8,
      paddingVertical: 5,
    },
    footerBtnGhostText: {
      fontSize: 11,
      fontWeight: '700',
      color: faint,
    },
  });
}

export default DaySheetScheduleEditor;
